<script setup lang="ts">
import { useAbility } from '@casl/vue'
import { getActivePinia } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { SiteTypeSelections } from '@/@vxapp/Sites/SiteOptions'
import { useSiteStore } from '@/@vxapp/Sites/useSiteStore'
import ConfirmActionDialog from '@/components/Dialogs/ConfirmActionDialog.vue'
import SiteListToolbar from '@/components/ServiceLists/Sites/SiteListToolbar.vue'
import SitesDataTable from '@/components/Tables/Sites/SitesDataTable.vue'

const sitesPageMeta = PageMeta.Sites as unknown as {
  List: { Meta: Record<string, unknown>; Title: string }
  Add: { Icon: string; Title: string; Tooltip?: string | null; Name: string }
  Search: { Icon: string; Title: string; Tooltip?: string | null }
  TableShortcuts: Record<string, unknown>
}

definePage({ meta: { ...PageMeta.Sites.List.Meta, pageTitle: PageMeta.Sites.List.Title } })

const siteStore = useSiteStore()
const route = useRoute()
const router = useRouter()
const { can } = useAbility()

const sitesDataTable = ref<InstanceType<typeof SitesDataTable> | null>(null)
const isRouteQueryReady = ref(false)
const isDeleteDialogVisible = ref(false)
const isDeleting = ref(false)
const selectedDeleteSiteGuid = ref<string | null>(null)
const deleteDialogDescription = ref('')

type SnackbarStore = {
  success: CallableFunction
  error: CallableFunction
}

const DEFAULT_QUERY_PARAMS = {
  ...PaginationField.QueryParams,
  hideInactive: true,
  siteType: undefined as string | undefined,
}

const MANAGED_QUERY_KEYS = ['page', 'limit', 'sort', 'search', 'hideInactive', 'siteType'] as const

function normalizeQueryValue(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : undefined
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return typeof value === 'string' ? value : undefined
}

function normalizeSearchQuery(value: unknown): string | undefined {
  const normalizedValue = normalizeQueryValue(value)?.trim()

  return normalizedValue ? normalizedValue : undefined
}

function normalizePositiveInteger(value: unknown, fallback: number): number {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeLimit(value: unknown): number {
  const parsedValue = Number.parseInt(normalizeQueryValue(value) ?? '', 10)

  if (parsedValue === -1) {
    return parsedValue
  }

  return normalizePositiveInteger(value, DEFAULT_QUERY_PARAMS.limit)
}

function normalizeSort(value: unknown): string {
  const normalizedValue = normalizeQueryValue(value)

  return normalizedValue === '+id' || normalizedValue === '-id'
    ? normalizedValue
    : DEFAULT_QUERY_PARAMS.sort
}

function normalizeHideInactive(value: unknown): boolean {
  const normalizedValue = normalizeQueryValue(value)

  if (normalizedValue === 'false') {
    return false
  }

  if (normalizedValue === 'true') {
    return true
  }

  return DEFAULT_QUERY_PARAMS.hideInactive
}

function normalizeSiteType(value: unknown): string | undefined {
  const normalizedValue = normalizeQueryValue(value)
  const allowedValues = SiteTypeSelections.map(selection => selection.value)

  return allowedValues.includes(normalizedValue as never) ? normalizedValue : undefined
}

function getNormalizedQueryParams(source: Record<string, unknown>) {
  return {
    page: normalizePositiveInteger(source.page, DEFAULT_QUERY_PARAMS.page),
    limit: normalizeLimit(source.limit),
    sort: normalizeSort(source.sort),
    search: normalizeSearchQuery(source.search),
    hideInactive: normalizeHideInactive(source.hideInactive),
    siteType: normalizeSiteType(source.siteType),
  }
}

function areQueryParamsEqual(
  left: ReturnType<typeof getNormalizedQueryParams>,
  right: ReturnType<typeof getNormalizedQueryParams>
) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search &&
    left.hideInactive === right.hideInactive &&
    left.siteType === right.siteType
  )
}

function applyRouteQueryParams() {
  const currentQueryParams = getNormalizedQueryParams(siteStore.queryParams)
  const routeQueryParams = getNormalizedQueryParams(route.query as Record<string, unknown>)

  if (areQueryParamsEqual(currentQueryParams, routeQueryParams)) {
    return false
  }

  siteStore.queryParams = {
    ...siteStore.queryParams,
    ...routeQueryParams,
  }

  return true
}

function buildRouteQuery() {
  const currentRouteQuery = { ...route.query }

  for (const key of MANAGED_QUERY_KEYS) {
    delete currentRouteQuery[key]
  }

  const queryParams = getNormalizedQueryParams(siteStore.queryParams)

  return {
    ...currentRouteQuery,
    ...(queryParams.page !== DEFAULT_QUERY_PARAMS.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== DEFAULT_QUERY_PARAMS.limit
      ? { limit: String(queryParams.limit) }
      : {}),
    ...(queryParams.sort !== DEFAULT_QUERY_PARAMS.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
    ...(queryParams.hideInactive !== DEFAULT_QUERY_PARAMS.hideInactive
      ? { hideInactive: String(queryParams.hideInactive) }
      : {}),
    ...(queryParams.siteType ? { siteType: queryParams.siteType } : {}),
  }
}

function areRouteQueriesEqual(left: Record<string, unknown>, right: Record<string, unknown>) {
  const leftKeys = Object.keys(left).sort()
  const rightKeys = Object.keys(right).sort()

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key])
}

async function syncRouteQuery() {
  const nextQuery = buildRouteQuery()

  if (areRouteQueriesEqual(route.query as Record<string, unknown>, nextQuery)) {
    return
  }

  await router.replace({ query: nextQuery })
}

onMounted(async () => {
  await siteStore.init()
  applyRouteQueryParams()
  isRouteQueryReady.value = true
  await sitesDataTable.value?.fetchSites()
})

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value) return
    if (!applyRouteQueryParams()) return

    await sitesDataTable.value?.fetchSites()
  },
  { deep: true }
)

watch(
  () => getNormalizedQueryParams(siteStore.queryParams),
  async () => {
    if (!isRouteQueryReady.value) return

    await syncRouteQuery()
  },
  { deep: true }
)

const resultCount = computed(() => Number(siteStore.paginationMeta?.entriesFound ?? 0))

function handleAddSite() {
  router.push({ name: sitesPageMeta.Add.Name } as never)
}

function handleSearchQuery(searchQuery: string) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    search: searchQuery.trim() || undefined,
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handleLimitChange(limit: number) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    limit,
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handleSortChange(sort: string) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    sort,
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handleSiteTypeUpdate(siteType: string | undefined) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    siteType,
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handleHideInactiveUpdate(hideInactive: boolean | null) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    hideInactive: Boolean(hideInactive),
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handlePageChange(page: number) {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    page,
  }
  sitesDataTable.value?.fetchSites()
}

function handleClearFilters() {
  siteStore.queryParams = {
    ...siteStore.queryParams,
    search: undefined,
    siteType: undefined,
    page: 1,
  }
  sitesDataTable.value?.fetchSites()
}

function handleRefresh() {
  sitesDataTable.value?.fetchSites()
}

function handleDeleteSite(siteGuid: string) {
  selectedDeleteSiteGuid.value = siteGuid
  deleteDialogDescription.value = `Delete site ${siteGuid}? This cannot be undone.`
  isDeleteDialogVisible.value = true
}

function getSnackbarStore() {
  const activePinia = getActivePinia() as unknown as { _s: Map<string, unknown> } | undefined
  const stores = activePinia?._s

  return stores?.get('NotificationsStore') as SnackbarStore | undefined
}

async function handleDeleteConfirmation(hasAgreed: boolean) {
  if (!hasAgreed) {
    isDeleteDialogVisible.value = false

    return
  }

  if (!selectedDeleteSiteGuid.value) return

  isDeleting.value = true

  const result = await siteStore.delete(selectedDeleteSiteGuid.value)
  const snackbarStore = getSnackbarStore()

  isDeleting.value = false

  if (result.responseCode === ResponseCode.OK || result.responseCode === ResponseCode.ACCEPTED) {
    snackbarStore?.success(
      result.message ??
        (result.responseCode === ResponseCode.ACCEPTED
          ? 'Site deletion has been accepted and is processing'
          : 'Site deleted successfully')
    )
    isDeleteDialogVisible.value = false
    selectedDeleteSiteGuid.value = null

    await sitesDataTable.value?.fetchSites()

    return
  }

  snackbarStore?.error(result.message ?? 'Failed to delete site')
}
</script>

<template>
  <main class="sites-workspace">
    <section class="sites-workspace__header">
      <div class="sites-workspace__intro">
        <span class="sites-workspace__eyebrow">
          <span aria-hidden="true" />
          Runtime directory
        </span>
        <h1>Sites</h1>
        <p>
          Inspect each destination through its live runtime contract, deployed theme, public brand,
          and current site health.
        </p>
      </div>

      <div class="sites-workspace__header-actions">
        <div class="sites-workspace__metric">
          <strong>{{ resultCount.toLocaleString() }}</strong>
          <span>{{ resultCount === 1 ? 'destination' : 'destinations' }}</span>
        </div>
        <VBtn
          v-if="can(A.CREATE, Subject.SITES)"
          class="sites-workspace__add-site"
          color="primary"
          prepend-icon="tabler-plus"
          size="large"
          @click="handleAddSite"
        >
          Add site
        </VBtn>
      </div>
    </section>

    <SiteListToolbar
      :is-loading="siteStore.isLoading"
      :query-params="siteStore.queryParams"
      :result-count="resultCount"
      @clear="handleClearFilters"
      @inactive-change="handleHideInactiveUpdate"
      @limit-change="handleLimitChange"
      @refresh="handleRefresh"
      @search="handleSearchQuery"
      @site-type-change="handleSiteTypeUpdate"
      @sort-change="handleSortChange"
    />

    <SitesDataTable
      ref="sitesDataTable"
      @delete-site="handleDeleteSite"
      @page-change="handlePageChange"
    />
  </main>

  <ConfirmActionDialog
    :is-dialog-visible="isDeleteDialogVisible"
    title="Delete Site"
    :description="deleteDialogDescription"
    submit-button-name="Delete"
    :is-loading="isDeleting"
    @update:has-agreed="handleDeleteConfirmation"
  />
</template>

<style scoped lang="scss">
.sites-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
}

.sites-workspace__header {
  display: grid;
  min-inline-size: 0;
  align-items: center;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  box-shadow:
    0 0 0 1px rgba(var(--v-border-color), var(--v-border-opacity)),
    0 2px 2px rgba(var(--v-shadow-key-umbra-color), 0.04),
    0 8px 8px -8px rgba(var(--v-shadow-key-umbra-color), 0.08);
  gap: 3rem;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: clamp(1.5rem, 4vw, 2.5rem);
}

.sites-workspace__intro {
  min-inline-size: 0;
}

.sites-workspace__eyebrow {
  display: flex;
  align-items: center;
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.68rem;
  font-weight: 500;
  gap: 0.45rem;
  text-transform: uppercase;
}

.sites-workspace__eyebrow span {
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
  block-size: 0.45rem;
  box-shadow: 0 0 0 3px rgba(var(--v-theme-success), 0.14);
  inline-size: 0.45rem;
}

.sites-workspace h1 {
  margin: 0.65rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(2.25rem, 5vw, 3rem);
  font-weight: 600;
  letter-spacing: -0.05em;
  line-height: 1;
}

.sites-workspace__intro p {
  max-inline-size: 45rem;
  margin: 0.8rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.92rem;
  line-height: 1.6;
}

.sites-workspace__header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sites-workspace__header-actions :deep(.sites-workspace__add-site.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.24);
  font-weight: 500;
}

.sites-workspace__metric {
  display: grid;
  min-inline-size: 6rem;
  gap: 0.1rem;
  justify-items: end;
}

.sites-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.62rem;
  font-weight: 500;
  text-transform: uppercase;
}

.sites-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.65rem;
  font-weight: 600;
  letter-spacing: -0.06em;
  line-height: 1;
}

@media (max-width: 767px) {
  .sites-workspace__header {
    align-items: start;
    grid-template-columns: minmax(0, 1fr);
    gap: 1.5rem;
  }

  .sites-workspace__header-actions {
    justify-content: space-between;
  }

  .sites-workspace__metric {
    justify-items: start;
  }
}

@media (max-width: 424px) {
  .sites-workspace__header-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .sites-workspace__header-actions :deep(.sites-workspace__add-site.v-btn) {
    inline-size: 100%;
  }
}
</style>
